import React, { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Collapse, Typography } from '@material-ui/core';
import { createStyles, fade, makeStyles, Theme } from '@material-ui/core/styles';
import styled from '@material-ui/core/styles/styled';
import { useTranslation } from 'react-i18next';

import ChevronTopIcon from '../../../../assets/icons/ChevronTopIcon';
import RatingIcon from '../../../../assets/icons/RatingIcon';
import { GET_LIST_REVIEWS_FOR_ENTITY } from '../../../../graphql/videos';
import {
  BIG_MARGIN,
  colorsPalette,
  LARGE_MARGIN,
  MIDDLE_MARGIN,
  SMALL_MARGIN,
  TINY_MARGIN,
} from '../../../../theme';
import { listReviewsArgs, Query, ReviewableType } from '../../../../types';
import Pagination, {
  usePagination,
} from '../../../SingleVideoPage/components/Pagination/Pagination';
import Rating from '../../../SingleVideoPage/components/Rating/Rating';
import ReviewForm from '../../../SingleVideoPage/components/ReviewForm/ReviewForm';
import ReviewMessage from '../../../SingleVideoPage/components/ReviewMessage/ReviewMessage';
import { updateReview } from '../../../SingleVideoPage/components/VideoTabs/VideoTabs';

const useStyles = makeStyles(() =>
  createStyles({
    ratingWrapper: {
      marginBottom: 0,
    },
  }),
);

const PAGE_STEP = 10;

const PathwayRating = ({ id }) => {
  const classes = useStyles();
  const [collapsed, setCollapsed] = useState(true);
  const { t } = useTranslation();
  const { currentItem, handlePrev, handleNext, setTotalItems, currentPage } = usePagination(
    PAGE_STEP,
  );

  const { data: reviewsList } = useQuery<Pick<Query, 'getListReviewsForEntity'>, listReviewsArgs>(
    GET_LIST_REVIEWS_FOR_ENTITY,
    {
      variables: {
        entity_type: ReviewableType.Pathway,
        entity_id: id || '',
        pagination_option: {
          page: currentPage,
          count_items_on_page: PAGE_STEP,
        },
      },
      skip: !id,
    },
  );

  const totalReviews = reviewsList?.getListReviewsForEntity?.count_reviews || 0;
  const rating = reviewsList?.getListReviewsForEntity?.general_star_rating;

  useEffect(() => {
    if (totalReviews) {
      setTotalItems(totalReviews);
    }
  }, [setTotalItems, totalReviews]);

  const updatedReviews = useMemo(
    () => reviewsList?.getListReviewsForEntity?.reviews?.map(updateReview),
    [reviewsList],
  );

  return (
    <Wrapper>
      <Row>
        <StyledRatingIcon />
        <TitleRow>
          <Title variant="h2">{t('page.pathway.whatLearnersSay')}</Title>
          <Rating name="totalRating" value={rating} wrapperClass={classes.ratingWrapper} />
        </TitleRow>
      </Row>
      <Message>{t('page.pathway.readFeedback')}</Message>
      <CollapseButton data-testid="toggleRating" onClick={() => setCollapsed((old) => !old)}>
        <StyledChevron toTop={!collapsed} />
        {collapsed ? t('page.pathway.showAllReviews') : t('page.pathway.hideAllReviews')}
      </CollapseButton>
      <Collapse data-testid="ratingCollapse" in={!collapsed}>
        <CollapseContent>
          <ReviewForm id={id} type={ReviewableType.Pathway} />
          {updatedReviews?.map((review, index) => (
            <ReviewMessage
              key={index}
              rating={review?.stars_rating}
              date={review?.created_at}
              name={review?.full_name}
              text={review?.comment}
            />
          ))}
          {Boolean(totalReviews > 0) && (
            <Pagination
              current={currentItem}
              total={totalReviews}
              step={PAGE_STEP}
              handlePrev={handlePrev}
              handleNext={handleNext}
            />
          )}
        </CollapseContent>
      </Collapse>
    </Wrapper>
  );
};

export default PathwayRating;

const TitleRow = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  flexDirection: 'column-reverse',
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    display: 'block',
  },
}));
const StyledChevron = styled(ChevronTopIcon)(({ toTop }: { toTop: boolean }) => ({
  width: 18,
  marginRight: TINY_MARGIN,
  transform: `rotate(${toTop ? 0 : 180}deg)`,
  transition: 'transform .3s',
  color: colorsPalette.primary,
}));
const CollapseContent = styled('div')(({ theme }: { theme: Theme }) => ({
  paddingTop: MIDDLE_MARGIN,
  [theme.breakpoints.up('md')]: {
    paddingTop: LARGE_MARGIN,
  },
}));
const CollapseButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  border: 'none',
  color: colorsPalette.white,
  background: 'transparent',
  fontSize: 16,
  fontWeight: 700,
  textTransform: 'uppercase',
  cursor: 'pointer',
});
const Wrapper = styled('div')(({ theme }: { theme: Theme }) => ({
  paddingTop: BIG_MARGIN,
  paddingBottom: SMALL_MARGIN,
  [theme.breakpoints.up('md')]: {
    paddingTop: LARGE_MARGIN,
    paddingBottom: 0,
  },
}));
const Title = styled(Typography)(({ theme }: { theme: Theme }) => ({
  width: '100%',
  marginTop: SMALL_MARGIN,
  marginBottom: TINY_MARGIN,
  paddingTop: SMALL_MARGIN,
  borderTop: '1px solid ' + fade(colorsPalette.white, 0.33),
  [theme.breakpoints.up('md')]: {
    marginTop: 0,
    paddingTop: 0,
    borderTop: 'none',
  },
}));
const Message = styled(Typography)(({ theme }: { theme: Theme }) => ({
  marginBottom: SMALL_MARGIN,
  fontSize: 16,
  color: colorsPalette.gray,
  [theme.breakpoints.up('md')]: {
    fontSize: 18,
  },
}));
const Row = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: SMALL_MARGIN,
  },
}));
const StyledRatingIcon = styled(RatingIcon)(({ theme }: { theme: Theme }) => ({
  marginBottom: TINY_MARGIN,
  [theme.breakpoints.up('md')]: {
    marginRight: MIDDLE_MARGIN,
    marginBottom: 0,
  },
}));
